<script setup lang="ts">

import { useStore } from "vuex"
import { computed, ref } from "vue"
import { useGetAllProjects } from "@/composables/aiChatFunctions"
import TeleportsAcMediumModal from "../../reusable/teleports/acMediumModal.vue"
import ManageProjectMembers from "./manageProjectMembers.vue"
import type { AICollabProject } from "@/types"
const emit = defineEmits(['projectChanged', 'createNewProject'])
const store = useStore()

const isEditingProjectMembers = ref(false)
const searchAreaDisplayed = ref(false)
const searchTerm = ref('')

const selectedProject = computed(() => store.getters['ai/selectedProject']);
const userProfile = computed(() => store.getters['user/userProfile']);

const {projects, isLoadingProjects, getProjects} = useGetAllProjects(userProfile.value.uid)

const filteredProjects = computed(() => {
    if (searchTerm.value === '') {
        return projects.value
    }
    return projects.value.filter((session) => {
        return session.name.toLowerCase().includes(searchTerm.value.toLowerCase())
    })
})

const refreshProjects = async () => {
    await getProjects()
}

const handleCreateNewProject = async () => {
    emit('createNewProject')
}

const projectChanged = (project: AICollabProject) => {
    emit('projectChanged', project)
}

defineExpose({
    refreshProjects
})

</script>

<template>
    <aside class="sidebar">
        <div class="sidebar-container">
            <div v-if="searchAreaDisplayed" class="search-area">
                <input @blur="searchTerm = ''; searchAreaDisplayed = false" v-model="searchTerm" type="text" placeholder="Projelerde Ara" />
            </div>
            <div v-else class="sidebar-actions">
                <img @click="searchAreaDisplayed = !searchAreaDisplayed" class="sidebar-action-icon" src="../../../assets/icons/search.png" alt="Search Chats" />
                <img v-if="userProfile.allowsEdit" @click="handleCreateNewProject" class="sidebar-action-icon" src="../../../assets/icons/add.png" alt="Add New Chat" />
            </div>
            <h1 class="chats">Projeler</h1>
            <div class="chats-container">
                <p class="text-left text-gray-400" v-if="filteredProjects.length === 0">
                    Hiçbir proje bulunamadı.
                </p>
                <ul v-else-if="!isLoadingProjects && filteredProjects.length > 0">
                    <li class="flex items-center justify-between" @click="projectChanged(project)" :class="{ 'selected': project.id === selectedProject?.id }" v-for="project in filteredProjects" :key="project.id">
                        {{ project.name }}
                        <div class="processor-container" v-if="project.isProcessingInfoBase">
                            <div class="processor"></div>
                        </div>
                        <span v-if="project.id === selectedProject?.id && userProfile.allowsEdit">
                            <img @click="isEditingProjectMembers = true" class="sidebar-action-icon" src="../../../assets/icons/list.png" alt="Manage Project Members" />
                        </span>
                    </li>
                </ul>
            </div>
        </div>        
        <transition
        enter-active-class="animate__animated animate__fadeIn"
        leave-active-class="animate__animated animate__fadeOut"
        > 
        <Teleport to=".global-container">
            <teleports-ac-medium-modal title="Proje Üyelerini Düzenle" width="50vw" height="60vh" v-if="isEditingProjectMembers" @modal-closed="isEditingProjectMembers = false; ">     
                <manage-project-members />
            </teleports-ac-medium-modal>
        </Teleport>
        </transition>

    </aside>
</template>



<style scoped>
.sidebar {
    display: flex;
    justify-content: flex-start;
    height: calc(100svh - 9rem);
    padding: 1.6rem 0 0;
    max-width: 29rem;
    flex-basis: 15%;
    flex-grow: 0;
    color: #f1eeed;
    flex-direction: column;
    background-color: rgb(249, 249, 249);
}

.search-area {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    width: 100%;
    padding: 0 2.4rem;
}

.search-area input {
    width: 100%;
    padding: 1.2rem 2.4rem;
    border-radius: 4px;
    border: 1px solid #000;
    outline: none;
    color: #000;
}

h1.chats{
    font-size: 1.8rem;
    font-weight: 600;
    cursor: pointer;
    margin-top: 1.6rem;
    padding: 0 2.4rem;
    color: #000;
}


.sidebar h2 {
    padding: 1rem 2.4rem;
    cursor: pointer;
}

.sidebar h3 {
    padding: 1rem 2.4rem;
    font-weight: 600;
    font-family: "Roboto", sans-serif;
    font-size: 1.3rem;
    margin-top: 1.6rem;
    text-transform: lowercase;
}

.sidebar-container {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: flex-start;
    height: 100%;
    width: 100%;
}

.chats-container {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: 100%;
    flex-basis: 100%;
    text-align: center;
    padding: 0 2.4rem;
    margin-top: 1.6rem;
}

.chats-container ul li{
    color: #000;
    font-weight: 400;
    text-align: left;
    font-size: 1.4rem;
    cursor: pointer;
    padding: 1.2rem 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 98%;
}

.chats-container ul li.selected {
    font-weight: 600;
}

.chats-container ul li:hover {
    background-color: #f1eeed;
    border-radius: 4px;
}
.sidebar-actions {
    display: flex;
    flex-direction: row;
    justify-content: flex-end;
    gap: 1.4rem;
    width: 100%;
    padding: 0 2.4rem;
}

.sidebar-action-icon {
    width: 1.8rem;
    height: 1.8rem;
    cursor: pointer;
    filter: brightness(0);
}

.processor-container {
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    width: 1.5rem;
    height: 1.5rem;
    border-radius: 50%;
    background-color: orangered;
    opacity: 0.5;
}

.processor {
    height: .7rem;
    aspect-ratio: 5;
    --c: #ffffff 90deg,#0000 0;
    background:
     conic-gradient(from 135deg at top   ,var(--c)),
     conic-gradient(from -45deg at bottom,var(--c)) 12.5% 100%;
    background-size: 20% 50%;
    background-repeat: repeat-x;
    /* Center the processor within its container */
    margin: 0 auto;
    /* Modified animation that keeps content visible within container */
    animation: l17 .4s infinite alternate;
}

@keyframes l17 {
    0% {
        transform: translateX(10%);
    }
    100% {
        transform: translateX(30%);
    }
}
@media screen and (max-width: 900px) {

    .sidebar h3:first-of-type {
        margin-top: 0;
    }
}
</style>